import type { CommonPowerSyncDatabase, SyncStatus } from '@powersync/common';
import { wrapPowerSyncWithDrizzle } from '@powersync/drizzle-driver';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { drizzleSchema } from './AppSchema';
import { ClerkConnector, isNoClerkSessionError } from './clerk/ClerkConnector';
import { connectVerified } from './connectVerified';
import { isRoutineSyncTokenExpiry } from './expiredSyncToken';
import { isTransientNetworkError } from './isTransientNetworkError';
import { describeError, powersyncLogger } from './logger';

type Props = {
  connector: ClerkConnector;
  database: CommonPowerSyncDatabase;
  /** Consuming app's package.json version, reported to PowerSync as connection metadata. */
  appVersion: string;
  /**
   * Runs after `init()` and before connecting. Anything that has to clear the database belongs
   * here, where it cannot race the sync stream — see `wipeLocalDataOnIdentityChange`.
   *
   * Read through a ref, so a caller that rebuilds the callback on every render (one closing over
   * the signed-in user, say) does not re-run the whole bootstrap.
   */
  prepareDatabase?: (database: CommonPowerSyncDatabase) => Promise<void>;
};

export class System {
  clerkConnector: ClerkConnector;
  powersync: CommonPowerSyncDatabase;
  /**
   * Typed with the schema generic rather than left to widen: without it `db.query` resolves to `{}`
   * and every relational query (`db.query.folders.findFirst(…)`) is a type error at the call site.
   */
  private _drizzleDb: ReturnType<typeof wrapPowerSyncWithDrizzle<typeof drizzleSchema>> | null = null;

  constructor(connector: ClerkConnector, database: CommonPowerSyncDatabase) {
    this.powersync = database;
    this.clerkConnector = connector;
  }

  public drizzleDb = () => {
    if (!this._drizzleDb) {
      this._drizzleDb = wrapPowerSyncWithDrizzle(this.powersync, { schema: drizzleSchema });
    }
    return this._drizzleDb;
  };
}

export const PowerSyncSystem = createContext<System | null>(null);

export const PowersyncSystemProvider: FC<PropsWithChildren<Props>> = ({
  children,
  connector,
  database,
  appVersion,
  prepareDatabase,
}) => {
  const [system] = useState(() => new System(connector, database));
  const prepareDatabaseRef = useRef(prepareDatabase);

  useEffect(() => {
    prepareDatabaseRef.current = prepareDatabase;
  }, [prepareDatabase]);

  useEffect(() => {
    // The database is a module-level singleton, so this effect deliberately does not
    // disconnect on unmount — a remount (StrictMode, HMR, a parent re-mounting) would
    // otherwise tear down the sync stream. Disconnecting belongs to sign-out, via
    // `wipeLocalData`.
    let cancelled = false;

    // `statusChanged` also fires on every download progress tick, so only the fields worth
    // reacting to are compared — otherwise an active sync buries everything else in the log.
    let lastStatusSnapshot = '';
    const logStatus = (status: SyncStatus) => {
      const context = {
        connected: status.connected,
        connecting: status.connecting,
        hasSynced: status.hasSynced,
        lastSyncedAt: status.lastSyncedAt?.toISOString(),
        uploadError: status.uploadError?.message,
        downloadError: status.downloadError?.message,
      };

      const snapshot = JSON.stringify([
        context.connected,
        context.connecting,
        context.hasSynced,
        context.uploadError,
        context.downloadError,
      ]);
      if (snapshot === lastStatusSnapshot) {
        return;
      }
      lastStatusSnapshot = snapshot;

      const syncError = status.uploadError ?? status.downloadError;
      if (syncError) {
        // A dropped connection is routine and the SDK retries it unaided, so it stays a breadcrumb
        // and only a failure that needs a human becomes an issue. The error is flattened into the
        // context by hand because a warning carries no `error` for the transport to report.
        if (isTransientNetworkError(syncError)) {
          powersyncLogger.warn('sync connection lost, retrying', {
            ...describeError(syncError),
            ...context,
          });
          return;
        }

        // Signed out, or signing out: there is nobody to sync for, and the SDK picks back up by
        // itself once a session exists again. A breadcrumb, for the same reason as above.
        if (isNoClerkSessionError(syncError)) {
          powersyncLogger.warn('sync paused, no Clerk session', context);
          return;
        }

        // A token that expired while the app was backgrounded: the SDK fetches a fresh one and
        // reconnects by itself. Only a run of these becomes an issue — see `expiredSyncToken`.
        if (isRoutineSyncTokenExpiry(syncError)) {
          powersyncLogger.warn('sync token expired, re-authenticating', {
            ...describeError(syncError),
            ...context,
          });
          return;
        }

        powersyncLogger.exception('sync degraded', syncError, context);
        return;
      }
      powersyncLogger.info('sync status changed', context);
    };

    const disposeStatusListener = system.powersync.registerListener({ statusChanged: logStatus });

    const bootstrap = async () => {
      await system.powersync.init();
      powersyncLogger.info('database initialized');

      // Deliberately not caught: this is where a database belonging to a different user is
      // wiped, and connecting anyway would sync the new session's data on top of the old one's
      // and serve both. An unsynced dashboard is the better failure — `bootstrap` reports it.
      await prepareDatabaseRef.current?.(system.powersync);

      // A failed probe is not fatal, so nothing below is skipped because of it. `connect()` owns
      // its own credential retry (`retryDelayMs`), which is what recovers a launch with no
      // network — bailing out here instead left the app disconnected until it was restarted.
      try {
        await connector.init();
        powersyncLogger.info('connector initialized');
      } catch (err) {
        if ((err as { code?: string }).code === 'clerk_offline') {
          powersyncLogger.warn('Clerk is offline, connecting anyway and retrying auth in the background');
        } else if (isNoClerkSessionError(err)) {
          powersyncLogger.warn('no Clerk session, connecting anyway and retrying auth in the background');
        } else {
          powersyncLogger.exception('connector initialization failed, connecting anyway', err);
        }
      }

      // Waits out any sign-out wipe and confirms the SDK accepted the request, because a connect
      // racing a wipe is abandoned silently with nothing left running to retry it.
      await connectVerified({
        database: system.powersync,
        connector: system.clerkConnector,
        appVersion,
        isCancelled: () => cancelled,
      });
    };

    bootstrap().catch((err) => {
      powersyncLogger.exception('failed to start PowerSync', err);
    });

    return () => {
      cancelled = true;
      disposeStatusListener();
    };
  }, [system, connector, appVersion]);

  return <PowerSyncSystem.Provider value={system}>{children}</PowerSyncSystem.Provider>;
};

export const useSystem = () => {
  const context = useContext(PowerSyncSystem);
  if (!context) {
    throw new Error('useSystem must be used within a PowerSyncSystemProvider');
  }
  return context;
};
